import React from 'react'

import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'

interface Props {
  rate: App.EnrichedPropertyRate
  /**
   * Opt-in bundles the customer currently has ticked on this rate. Their cost is added on top of
   * the rate price, which is what the customer will be charged at checkout.
   */
  tickedBundles: Array<App.Property.OptionalBundle>
  /**
   * Metasearch discount to subtract from the rate price before adding bundles. Bedbank rates pass
   * their resolved discount; LPC rates leave this unset (their discount is already in the totals).
   */
  discountAmount?: number
}

/**
 * Cash price for a property rate tile, including any opt-in bundles the customer has ticked.
 *
 * Opt-in bundle cost is applied at display time only — writing it back into `rate.totals` would
 * double-count it in every other consumer (margin, GTM, agent commission, price details). Points
 * and burn pricing deliberately exclude it too, since the burn calculation was quoted against the
 * hotel-only price; those render via `OfferPageBurnPriceDisplay` rather than this component.
 */
function PropertyRateOptionPrice(props: Props) {
  const { rate, tickedBundles, discountAmount = 0 } = props

  const tickedBundlesTotalPrice = tickedBundles.reduce((total, bundle) => total + bundle.totalPrice, 0)
  const price = Math.max(rate.totals.displayPrice - discountAmount, 0) + tickedBundlesTotalPrice
  const memberPrice = rate.luxPlusTotals?.displayPrice ?
    Math.max(rate.luxPlusTotals.displayPrice - discountAmount, 0) + tickedBundlesTotalPrice :
    undefined

  return <LuxPlusPriceGuard
    showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
    fallbackPrice={<PriceRowPrice
      data-testid="property-package-price"
      size="M"
      price={price}
      saleUnit="total"
    />}
  >
    <LuxPlusPriceStack
      size="M"
      testid="property-package-price"
      price={price}
      memberPrice={memberPrice ?? 0}
      saleUnit="total"
    />
  </LuxPlusPriceGuard>
}

export default PropertyRateOptionPrice
